import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trophy, ChevronDown, ChevronRight } from 'lucide-react';
import { api, type ClanRollupClan } from '../lib/api';
import { displayClanName } from '../lib/displayName';
import { CollapsibleSection } from './CollapsibleSection';
import { HelpText } from './HelpText';

// ─────────────────────────────────────────────────────────────────────────────
// ClanRollupPanel — M3 (comp-lifecycle-pipeline, clan rollup): "bring the
// stars back to the clan" (Lisa, 2026-07-10), the final wave of the
// multi-team epic. A clan may enter more than one of its named teams into a
// comp (M1) and each is tracked as its own distinct competitor throughout the
// stage pipeline (M2 — e.g. "Beta (Alpha)" and "Charlie (Alpha)" can sit in
// different divisions with entirely independent records). This panel is the
// OTHER half of that story: a comp-level, read-only board showing each
// clan's COMBINED honours — every team it fielded, summed back up under the
// clan.
//
// Placement: a sibling of StagePipelinePanel inside CompetitionCreateForm's
// edit mode (not its own wizard step, not nested inside a per-stage card) —
// the pipeline panel is already comp-scoped with per-stage children; this is
// "what the pipeline produces, summed", so it reads naturally right below it
// in the same scroll context. Self-contained (owns its own CollapsibleSection)
// so the call site is a one-line mount, same as StagePipelinePanel itself.
//
// Deliberately NOT StandingsTable — an entry row here carries fields
// StandingsTable was never built to show (divisionName/divisionRank/
// isChampion), and forcing those into StandingsTable's SeedingStandingRow
// shape would mean fabricating gameWins/gameLosses/totalScore zeros just to
// satisfy a type that doesn't fit. A small bespoke table (same Pos-less
// Team/P/W/L/Pts visual language) keeps the house idiom without faking data.
// ─────────────────────────────────────────────────────────────────────────────

export function ClanRollupPanel({ compId }: { compId: string }) {
  const { data: rollup } = useQuery({
    queryKey: ['clanRollup', compId],
    queryFn: () => api.clanRollup(compId),
    enabled: !!compId,
  });

  // Same "only materialize with a reason to show it" philosophy
  // StagePipelinePanel's own header note documents — a comp with no
  // enrolled entries at all shows nothing, not permanent empty-state clutter.
  if (!rollup || rollup.clans.length === 0) return null;

  return (
    <CollapsibleSection
      title="Clan Standings"
      hint="Combined honours across every stage — teams that entered more than one squad roll up here"
      icon={Trophy}
    >
      <HelpText>
        <p className="mb-2 text-[11px] text-ink-meta">
          Teams compete individually — their results also roll up to their clan. A clan's best result is
          whichever of its teams did best: a stage champion always outranks a division finish, which
          outranks points alone.
        </p>
      </HelpText>
      <div className="space-y-2">
        {rollup.clans.map((clan) => (
          <ClanRollupRow key={clan.clanId} clan={clan} />
        ))}
      </div>
    </CollapsibleSection>
  );
}

// Extracted so the list `.map()` above never puts hooks in a loop — same
// idiom DivisionsScoreboardPanel's own DivisionCard extraction follows.
function ClanRollupRow({ clan }: { clan: ClanRollupClan }) {
  const [expanded, setExpanded] = useState(false);
  const multiEntry = clan.entries.length > 1;

  return (
    <div className="rounded-lg bg-white/[0.03] p-2.5">
      {/* Mobile: stack the clan name onto its own line, stats+placing onto a
          second — the name div previously shared one row with a shrink-0
          stats block whose bestResult.label (e.g. "Div 2 · 2nd
          (DEMO_JSOC_2613)") never shrinks, so on a narrow phone it claimed
          the row and squeezed the clan name to zero width. sm:+ reverts to
          the exact original single-row layout. */}
      <button
        type="button"
        onClick={() => multiEntry && setExpanded((o) => !o)}
        className="flex w-full flex-col gap-1.5 text-left sm:flex-row sm:items-center sm:justify-between sm:gap-2"
        disabled={!multiEntry}
      >
        <div className="flex min-w-0 items-center gap-2">
          {multiEntry && (expanded
            ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-ink-meta" />
            : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ink-meta" />)}
          <span className="truncate text-xs font-semibold text-ink-primary">{displayClanName(clan.clanName)}</span>
          {multiEntry && <span className="shrink-0 text-[10px] text-ink-meta">{clan.entries.length} teams</span>}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-secondary sm:flex-nowrap sm:shrink-0">
          <span>{clan.totals.played}P</span>
          <span>{clan.totals.wins}W</span>
          <span>{clan.totals.losses}L</span>
          <span className="font-semibold text-ink-primary">{clan.totals.points} pts</span>
          <span className="text-ink-primary">{clan.bestResult.label}</span>
        </div>
      </button>

      {expanded && multiEntry && (
        <div className="mt-2 overflow-x-auto border-t border-white/[0.06] pt-2">
          <table className="w-full min-w-[480px] text-xs">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-ink-meta">
                <th className="py-1">Team</th>
                <th className="w-10 py-1 text-center">P</th>
                <th className="w-10 py-1 text-center">W</th>
                <th className="w-10 py-1 text-center">L</th>
                <th className="w-12 py-1 text-right">Pts</th>
                <th className="py-1 text-right">Result</th>
              </tr>
            </thead>
            <tbody>
              {clan.entries.map((entry) => (
                <tr key={entry.entryName} className="border-t border-white/[0.05]">
                  <td className="py-1.5 text-ink-primary">{displayClanName(entry.entryName)}</td>
                  <td className="py-1.5 text-center text-ink-secondary">{entry.played}</td>
                  <td className="py-1.5 text-center text-ink-secondary">{entry.wins}</td>
                  <td className="py-1.5 text-center text-ink-secondary">{entry.losses}</td>
                  <td className="py-1.5 text-right font-semibold text-ink-primary">{entry.points}</td>
                  <td className="py-1.5 text-right text-ink-meta">
                    {entry.isChampion
                      ? '🏆 Champion'
                      : entry.divisionName
                        ? `${entry.divisionName} · #${entry.divisionRank}`
                        : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
